import type { Theme } from '@goodtime/engine';
import { buildSlotAssets } from './slotAssets';

const ASSETS = buildSlotAssets('wizard', {
  cherry:  'glowing_rune',
  lemon:   'potion_vial',
  orange:  'wand',
  bell:    'fairy_ring',
  star:    'spellbook_wild',
  seven:   'owl_familiar',
  gem:     'candelabra',
  scatter: 'crystal_ball_scatter',
  money:   'raven',
});

/** Wizard's Tower — Kingdom alt slot. Purple/teal mystical palette,
 *  arcane/library iconography. Symbol-id mapping mirrors
 *  {@link FOREST_THEME}.
 *
 *  <p><b>Mechanical personality — "Bonus Chase":</b>
 *  <ul>
 *    <li>Highest scatter weight in the deck (3.0 vs Forest's 1.0).
 *        The crystal ball drops onto the reels often, triggering
 *        free spins / bonus rounds more frequently than any other
 *        theme.
 *    <li>Spellbook wild is also weighted high (3.0) — the wizard's
 *        magic substitutes generously across paylines.
 *    <li>Wheel skews to x5 — when the bonus does land, it pays
 *        meaningfully. Few zeros (one), no x25 jackpot — the
 *        big-money pull isn't the wheel, it's the bonus game.
 *  </ul>
 *  Intended feel: the wizard is generous with magic. You hit bonus
 *  rounds more often than feels statistically reasonable. */
export const WIZARD_THEME: Theme = {
  id: 'wizard',
  name: "Wizard's Tower",
  palette: {
    accent: 0x5e3a8c,         // mystic purple
    secondary: 0x56d9c5,      // teal glow
    warm: 0xffe49a,           // candle warm
    frameFill: 0x1a0e2a,
    bgGradientTop: 0x0e081a,
    bgGradientBottom: 0x3a1c5a,
    dimOverlay: 0x05080d,
  },
  slot: {
    symbols: [
      // Scatter + wild both weighted at 3 (vs Forest's 1 and 2). The
      // wizard's bonus game lands often, and his spellbook substitutes
      // freely. The trade is dropping money (raven) to 1 — fewer
      // top-tier in-line wins, but bonus mode picks up the slack.
      { id: 'cherry',  label: 'Rune',         glyph: '✶',  tint: 0x56d9c5, weight: 4, textureAlias: 'wizard:cherry',
        winVideoAlias: 'wizard:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Potion',       glyph: '🧪', tint: 0x5e3a8c, weight: 4, textureAlias: 'wizard:lemon',
        winVideoAlias: 'wizard:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Wand',         glyph: '🪄', tint: 0x56d9c5, weight: 3, textureAlias: 'wizard:orange',
        winVideoAlias: 'wizard:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Fairy Ring',   glyph: '🍄', tint: 0x5e3a8c, weight: 3, textureAlias: 'wizard:bell',
        winVideoAlias: 'wizard:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Spellbook',    glyph: '📖', tint: 0xe8b83a, weight: 3, textureAlias: 'wizard:star', // wild — generous
        winVideoAlias: 'wizard:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Owl',          glyph: '🦉', tint: 0x5e3a8c, weight: 1, textureAlias: 'wizard:seven',
        winVideoAlias: 'wizard:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'Candelabra',   glyph: '🕯️', tint: 0xe8b83a, weight: 1, textureAlias: 'wizard:gem',
        winVideoAlias: 'wizard:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Crystal Ball', glyph: '🔮', tint: 0x56d9c5, weight: 3, textureAlias: 'wizard:scatter', // scatter — generous
        winVideoAlias: 'wizard:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Raven',        glyph: '🐦‍⬛', tint: 0x1a0e2a, weight: 1,
        textureAlias: 'wizard:money',
        winVideoAlias: 'wizard:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    // x5 dominant — when the wheel pays, it pays well. The big-money
    // moments are meant to come from frequent bonus rounds (scatter
    // trigger weight is 3), not from rare wheel jackpots. Top caps
    // at x10; one zero segment preserves a sliver of risk.
    segments: [
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0x56d9c5, multiplier: 2,  weight: 3 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xffe49a, multiplier: 5,  weight: 4 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0xe8b83a, multiplier: 3,  weight: 3 },
      { id: 'x5-b',   kind: 'multiplier', label: '×5',  tint: 0xffe49a, multiplier: 5,  weight: 4 },
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0x5e3a8c, multiplier: 1,  weight: 3 },
      { id: 'x5-c',   kind: 'multiplier', label: '×5',  tint: 0xffe49a, multiplier: 5,  weight: 3 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0x56d9c5, multiplier: 2,  weight: 3 },
      { id: 'x0-a',   kind: 'multiplier', label: '✕',   tint: 0x1a0e2a, multiplier: 0,  weight: 1 },
      { id: 'x3-b',   kind: 'multiplier', label: '×3',  tint: 0xe8b83a, multiplier: 3,  weight: 3 },
      { id: 'x5-d',   kind: 'multiplier', label: '×5',  tint: 0xffe49a, multiplier: 5,  weight: 3 },
      { id: 'x10-a',  kind: 'multiplier', label: '×10', tint: 0x56d9c5, multiplier: 10, weight: 2 },
      { id: 'x2-c',   kind: 'multiplier', label: '×2',  tint: 0x56d9c5, multiplier: 2,  weight: 3 },
    ],
  },
  assets: {
    slot: ASSETS.slot,
    slotWin: ASSETS.slotWin,
    scene: {
      background:       { alias: 'wizard:bg',         path: 'environment/wizard.png' },
      columnBackground: { alias: 'wizard:column_bg',  path: 'reel/wizard_column_bg.png' },
      border:           { alias: 'wizard:border',     path: 'brand/border_forest.png' },
    },
  },
};
